<script setup lang="ts">
import { ref, watch, onMounted } from "vue";
import { Permission } from "../../../../../types/auth";
import ItemQuantityPricesForm from "./ItemQuantityPricesForm.vue";
import { FilterField, TableField } from "../../../../../components/table/table";
import Buttons from "../../../../../components/ui/form/Buttons.vue";
import ListTemplate from "../../../../../components/ui/common/ListTemplate.vue";

const props = defineProps<{
  data?: any | [];
  permissions?: Permission[];
  item?: any | null;
}>();

const endPoint = "/clients/inventory/item-management/items/quantity-price-";
const title = "Quantity Prices";
const selectedData = ref<any | null>(null);
const showForm = ref(false);
const formMode = ref<
  "create" | "edit" | "view" | "showQuickView" | "toggleStatus"
>("view");
const refreshData = ref(false);

const rows = ref<any[]>(props.data);

const fields: TableField[] = [
  {
    name: "price_list",
    caption: "Price List",
    sortable: true,
    fieldType: "text",
  },
  {
    name: "quantity",
    caption: "Quantity",
    sortable: true,
    fieldType: "number",
  },
  {
    name: "selling_price",
    caption: "Price",
    sortable: true,
    fieldType: "currency",
  },
  {
    name: "packing_price",
    caption: "Packing Price",
    sortable: true,
    fieldType: "currency",
  },
  {
    name: "delivery_charges",
    caption: "Delivery Charges",
    sortable: true,
    fieldType: "currency",
  },
  {
    name: "free_delivery_amount",
    caption: "Free Delivery Amount",
    sortable: true,
    fieldType: "currency",
  },
  { name: "points", caption: "Points", sortable: true, fieldType: "number" },
  {
    name: "always_charge_packing",
    caption: "Always Charge for Packing",
    sortable: true,
    fieldType: "boolean",
  },
  {
    name: "always_charge_delivery",
    caption: "Always Charge for Delivery",
    sortable: true,
    fieldType: "boolean",
  },
  { name: "active", caption: "active", sortable: false, fieldType: "status" },
];

const filterFields: FilterField[] = [
  {
    name: "active",
    label: "Status",
    type: "select",
    value: "",
    options: [
      { id: "1", name: "Active" },
      { id: "0", name: "Inactive" },
    ],
  },
];

const changeMode = (
  mode: "create" | "edit" | "view" | "showQuickView" | "toggleStatus"
) => {
  formMode.value = mode;
  showForm.value = mode === "view" || mode === "edit" || mode === "create";
};

const handleCreate = () => {
  selectedData.value = null;
  formMode.value = "create";
  showForm.value = true;
};

const handleData = (data: any) => {
  selectedData.value = data;
};

const handleSave = (data: any) => {
  rows.value = data;
};

onMounted(() => {
  rows.value = props.data;
});

watch(
  () => props.data,
  () => {
    rows.value = props.data;
  },
  { immediate: true }
);
</script>

<template>
  <ListTemplate
    :endPoint="endPoint"
    :title="title"
    :fields="fields"
    :actions="[]"
    :filterFields="filterFields"
    :tabList="[]"
    :refreshData="refreshData"
    @mode="changeMode"
    @show="showForm = true"
    :showForm="showForm"
    @data="handleData"
    :data="rows"
    :remote="false"
    :showHeader="false"
    :showQuickTour="false"
    :showQuickView="false"
    :showPagination="false"
    :ignore-max-height="true"
  >
    <template #header-actions>
      <Buttons icon="Plus" caption="New Price" @click="handleCreate" />
    </template>
    <template #form-view> </template>
  </ListTemplate>
  <ItemQuantityPricesForm
    :permissions="permissions"
    v-if="showForm"
    :item="item"
    :data="selectedData"
    :end-point="endPoint"
    :title="title"
    :mode="formMode"
    @save="handleSave"
    @close="showForm = !showForm"
  />
</template>
